import React, { useState, useEffect } from "react";
import CustomInput from "../components/common/CustomInput";
import CustomModal from "../components/common/CustomModal";
import CustomButton from "../components/common/CustomButton";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  addAction,
  updateAction,
  deleteAction,
  getActions,
} from "../utils/actionUtils";
import {
  TruckIcon,
  WrenchIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CogIcon,
} from "@heroicons/react/24/outline";

const styles = {
  safeArea: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    padding: "16px 24px",
    paddingTop: 16,
    minHeight: "100vh",
    maxWidth: 1400,
    margin: "0 auto",
  },
  titleContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 24,
    animation: "fadeIn 0.5s ease-in-out",
  },
  title: {
    fontSize: 32,
    color: COLORS.text,
    fontFamily: FONTS.bold,
    fontWeight: 700,
  },
  searchInput: {
    marginBottom: 16,
  },
  actionItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: 12,
    marginBottom: 16,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    border: `1px solid ${COLORS.tertiary}`,
    transition: "all 0.3s ease",
    animation: "fadeIn 0.5s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: `0 6px 16px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  actionInfo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  actionIcon: {
    width: 28,
    height: 28,
    color: COLORS.primary,
  },
  actionDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  actionName: {
    fontSize: 20,
    color: COLORS.text,
    fontFamily: FONTS.bold,
    fontWeight: 600,
  },
  actionDescription: {
    fontSize: 16,
    color: COLORS.gray,
    fontFamily: FONTS.regularInter,
  },
  actionRoles: {
    fontSize: 16,
    color: COLORS.gray,
    fontFamily: FONTS.regularInter,
  },
  actionsContainer: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  actionButton: {
    padding: 10,
    borderRadius: 10,
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.primary,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  deleteButton: {
    "&:hover": {
      backgroundColor: COLORS.error,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  editPriceButton: {
    padding: 6,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.primary,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  deletePriceButton: {
    padding: 6,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.error,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  addButton: {
    position: "fixed",
    bottom: 32,
    right: 32,
    border: "none",
    borderRadius: 32,
    width: 64,
    height: 64,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.primary,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
    cursor: "pointer",
    color: COLORS.white,
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: COLORS.secondary,
      color: COLORS.primary,
      transform: "scale(1.1)",
      boxShadow: `0 6px 20px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.3)`,
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  },
  emptyText: {
    fontSize: 18,
    color: COLORS.gray,
    textAlign: "center",
    marginTop: 32,
    fontFamily: FONTS.regularInter,
  },
  switchContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    padding: 12,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    border: `1px solid ${COLORS.gray}`,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
  switchLabel: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  switchIcon: {
    width: 20,
    height: 20,
    color: COLORS.primary,
  },
  priceInputWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  priceInputLeft: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  priceInput: {
    flex: 1,
  },
  selectContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  selectLabel: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
    fontWeight: 500,
    marginBottom: 8,
  },
  select: {
    width: "100%",
    height: 56,
    padding: "0 12px",
    borderRadius: 8,
    border: `1px solid ${COLORS.gray}`,
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    backgroundColor: COLORS.white,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: COLORS.primary,
      boxShadow: `0 4px 12px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  addPriceButtonTall: {
    width: 48,
    height: 48,
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.gray}`,
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: COLORS.primary,
      borderColor: COLORS.primary,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  priceList: {
    marginTop: 8,
    marginBottom: 12,
  },
  priceItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
    marginBottom: 8,
    gap: 12,
  },
  priceActions: {
    display: "flex",
    gap: 8,
  },
  modalContent: {
    maxHeight: "70vh",
    overflowY: "auto",
    paddingRight: 4,
  },
  loader: {
    fontSize: 18,
    color: COLORS.text,
    textAlign: "center",
    marginVertical: 32,
    fontFamily: FONTS.regularInter,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  loadingSpinner: {
    border: `3px solid ${COLORS.gray}`,
    borderTop: `3px solid ${COLORS.primary}`,
    borderRadius: "50%",
    width: 24,
    height: 24,
    animation: "spin 1s linear infinite",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@media (max-width: 768px)": {
    container: {
      padding: "12px 16px",
      paddingTop: 16,
    },
    title: {
      fontSize: 28,
    },
    actionItem: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 12,
      padding: 16,
    },
    actionsContainer: {
      width: "100%",
      justifyContent: "flex-end",
    },
    addButton: {
      bottom: 16,
      right: 16,
      width: 56,
      height: 56,
    },
    priceInputWrapper: {
      flexDirection: "column",
      alignItems: "stretch",
    },
    addPriceButtonTall: {
      width: "100%",
      height: 48,
      marginTop: 8,
    },
  },
};

const AdminActionManagementScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalVisible, setModalVisible] = useState(false);
  const [isPriceModalVisible, setPriceModalVisible] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [editingPriceAction, setEditingPriceAction] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [prices, setPrices] = useState({
    bag: "",
    gallon: "",
    hour: "",
    trip: "",
    day: "",
  });
  const [unit, setUnit] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [editPriceUnit, setEditPriceUnit] = useState("");
  const [editPriceValue, setEditPriceValue] = useState("");
  const [roles, setRoles] = useState({
    isDriver: false,
    isEquipmentMan: false,
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const units = ["bag", "gallon", "hour", "trip", "day"];

  const fetchActions = async () => {
    try {
      const actionList = await getActions();
      // Sort actions alphabetically by name
      const sortedActions = actionList.sort((a, b) =>
        (a.name || "Unnamed").localeCompare(b.name || "Unnamed")
      );
      setActions(sortedActions);
    } catch (error) {
      alert("Failed to fetch actions");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchActions();
  }, []);

  const handleAddPrice = () => {
    if (!unit || !priceInput) {
      alert("Please select a unit and enter a price");
      return;
    }
    const priceValue = parseFloat(priceInput);
    if (isNaN(priceValue) || priceValue < 0) {
      alert("Please enter a valid price");
      return;
    }
    setPrices((prev) => ({ ...prev, [unit]: priceValue }));
    setPriceInput("");
    setUnit("");
  };

  const handleDeletePrice = async (unitToDelete) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the price for ${unitToDelete}?`
      )
    ) {
      return;
    }
    if (editingPriceAction) {
      setLoading(true);
      try {
        const updatedPrices = { ...editingPriceAction.prices };
        delete updatedPrices[unitToDelete];
        await updateAction(editingPriceAction.id, {
          ...editingPriceAction,
          prices: updatedPrices,
        });
        alert("Price deleted successfully");
        setPriceModalVisible(false);
        setEditPriceUnit("");
        setEditPriceValue("");
        setEditingPriceAction(null);
        await fetchActions();
      } catch (error) {
        alert("Error: " + error.message);
      } finally {
        setLoading(false);
      }
    } else {
      setPrices((prev) => {
        const newPrices = { ...prev };
        delete newPrices[unitToDelete];
        return newPrices;
      });
    }
  };

  const handleEditPriceInModal = (unit) => {
    setEditPriceUnit(unit);
    setEditPriceValue(prices[unit]?.toString() || "");
    setPriceModalVisible(true);
  };

  const handleSavePrice = async () => {
    if (!editPriceUnit || !editPriceValue) {
      alert("Please select a unit and enter a price");
      return;
    }
    const priceValue = parseFloat(editPriceValue);
    if (isNaN(priceValue) || priceValue < 0) {
      alert("Please enter a valid price");
      return;
    }
    if (editingPriceAction) {
      setLoading(true);
      try {
        const updatedPrices = {
          ...editingPriceAction.prices,
          [editPriceUnit]: priceValue,
        };
        await updateAction(editingPriceAction.id, {
          ...editingPriceAction,
          prices: updatedPrices,
        });
        alert("Price updated successfully");
        setPriceModalVisible(false);
        setEditPriceUnit("");
        setEditPriceValue("");
        setEditingPriceAction(null);
        await fetchActions();
      } catch (error) {
        alert("Error: " + error.message);
      } finally {
        setLoading(false);
      }
    } else {
      setPrices((prev) => ({ ...prev, [editPriceUnit]: priceValue }));
      setPriceModalVisible(false);
      setEditPriceUnit("");
      setEditPriceValue("");
    }
  };

  const handleSaveAction = async () => {
    if (!name || Object.values(prices).every((price) => price === "")) {
      alert("Please fill in name and add at least one price");
      return;
    }
    setLoading(true);
    try {
      const actionData = {
        name,
        description,
        prices,
        unit,
        roles,
      };
      if (editingAction) {
        await updateAction(editingAction.id, actionData);
        alert("Action updated successfully");
      } else {
        await addAction(actionData);
        alert("Action added successfully");
      }
      setName("");
      setDescription("");
      setPrices({ bag: "", gallon: "", hour: "", trip: "", day: "" });
      setUnit("");
      setPriceInput("");
      setRoles({ isDriver: false, isEquipmentMan: false });
      setEditingAction(null);
      setModalVisible(false);
      await fetchActions();
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAction = (action) => {
    setEditingAction(action);
    setName(action.name);
    setDescription(action.description);
    setPrices(
      action.prices || { bag: "", gallon: "", hour: "", trip: "", day: "" }
    );
    setUnit(action.unit || "");
    setRoles(action.roles);
    setPriceInput("");
    setModalVisible(true);
  };

  const handleDeleteAction = async (id) => {
    if (!window.confirm("Are you sure you want to delete this action?")) {
      return;
    }
    setDeletingId(id);
    try {
      await deleteAction(id);
      alert("Action deleted successfully");
      await fetchActions();
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  // Filter actions based on search query
  const filteredActions = (actions || []).filter((action) =>
    (action.name || "Unnamed").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderActionItem = (item) => (
    <div style={styles.actionItem} key={item.id}>
      <div style={styles.actionInfo}>
        <CogIcon style={styles.actionIcon} />
        <div style={styles.actionDetails}>
          <span style={styles.actionName}>{item.name}</span>
          <span style={styles.actionDescription}>{item.description}</span>
          <span style={styles.actionRoles}>
            {item.roles.isDriver && item.roles.isEquipmentMan
              ? "Driver & Equipment Man"
              : item.roles.isDriver
              ? "Driver"
              : item.roles.isEquipmentMan
              ? "Equipment Man"
              : "No roles assigned"}
            {(item.roles.isDriver || item.roles.isEquipmentMan) && " ("}
            {item.roles.isDriver && (
              <TruckIcon
                style={{
                  ...styles.switchIcon,
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              />
            )}
            {item.roles.isDriver && item.roles.isEquipmentMan && ", "}
            {item.roles.isEquipmentMan && (
              <WrenchIcon
                style={{
                  ...styles.switchIcon,
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              />
            )}
            {(item.roles.isDriver || item.roles.isEquipmentMan) && ")"}
          </span>
        </div>
      </div>
      <div style={styles.actionsContainer}>
        <button
          style={{ ...styles.actionButton }}
          onClick={() => handleEditAction(item)}
        >
          <PencilIcon width={24} height={24} stroke={COLORS.primary} />
        </button>
        <button
          style={{ ...styles.actionButton, ...styles.deleteButton }}
          onClick={() => handleDeleteAction(item.id)}
          disabled={deletingId === item.id}
        >
          {deletingId === item.id ? (
            <div style={styles.loadingSpinner}></div>
          ) : (
            <TrashIcon width={24} height={24} stroke={COLORS.error} />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div style={styles.safeArea}>
      <div style={styles.container}>
        <div style={styles.titleContainer}>
          <h1 style={styles.title}>Actions Management</h1>
        </div>
        <CustomInput
          value={searchQuery}
          onChangeText={setSearchQuery}
          icon="search"
          placeholder="Search by action name"
          style={styles.searchInput}
        />
        {fetching ? (
          <div style={styles.loader}>
            <div style={styles.loadingSpinner}></div>
            <span>Loading...</span>
          </div>
        ) : (
          <div>
            {filteredActions.length === 0 ? (
              <span style={styles.emptyText}>No actions available</span>
            ) : (
              filteredActions.map(renderActionItem)
            )}
          </div>
        )}
        <button
          style={styles.addButton}
          onClick={() => {
            setEditingAction(null);
            setName("");
            setDescription("");
            setPrices({ bag: "", gallon: "", hour: "", trip: "", day: "" });
            setUnit("");
            setPriceInput("");
            setRoles({ isDriver: false, isEquipmentMan: false });
            setModalVisible(true);
          }}
          disabled={loading}
        >
          <PlusIcon width={30} height={30} stroke={COLORS.white} />
        </button>
        <CustomModal
          isVisible={isModalVisible}
          onClose={() => setModalVisible(false)}
          title={editingAction ? "Edit Action" : "Add Action"}
          style={{
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.gray}`,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
            borderRadius: 12,
            padding: 16,
            maxWidth: 500,
            width: "90%",
            margin: "0 auto",
          }}
        >
          <div style={styles.modalContent}>
            <CustomInput
              label="Action Name"
              value={name}
              onChangeText={setName}
              icon="label"
              placeholder="Enter action name"
              style={{ marginBottom: 12 }}
            />
            <CustomInput
              label="Description"
              value={description}
              onChangeText={setDescription}
              icon="description"
              placeholder="Enter action description"
              multiline
              style={{ marginBottom: 12 }}
            />
            <div style={styles.priceInputWrapper}>
              <div style={styles.priceInputLeft}>
                <div style={styles.selectContainer}>
                  <span style={styles.selectLabel}>Unit</span>
                  <select
                    style={styles.select}
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                  >
                    <option value="" disabled>
                      Select unit
                    </option>
                    {units.map((u) => (
                      <option key={u} value={u}>
                        {u.charAt(0).toUpperCase() + u.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.priceInput}>
                  <CustomInput
                    label="Price"
                    value={priceInput}
                    onChangeText={setPriceInput}
                    icon="attach-money"
                    type="number"
                    placeholder="Enter price"
                  />
                </div>
              </div>
              <button
                style={styles.addPriceButtonTall}
                onClick={handleAddPrice}
              >
                <PlusIcon width={24} height={24} stroke={COLORS.primary} />
              </button>
            </div>
            <div style={styles.priceList}>
              {Object.entries(prices)
                .filter(([_, price]) => price !== "" && price !== undefined)
                .map(([u, price]) => (
                  <div key={u} style={styles.priceItem}>
                    <span>{`${
                      u.charAt(0).toUpperCase() + u.slice(1)
                    }: $${price}`}</span>
                    <div style={styles.priceActions}>
                      <button
                        style={styles.editPriceButton}
                        onClick={() => handleEditPriceInModal(u)}
                      >
                        <PencilIcon
                          width={16}
                          height={16}
                          stroke={COLORS.primary}
                        />
                      </button>
                      <button
                        style={styles.deletePriceButton}
                        onClick={() => handleDeletePrice(u)}
                      >
                        <TrashIcon
                          width={16}
                          height={16}
                          stroke={COLORS.error}
                        />
                      </button>
                    </div>
                  </div>
                ))}
            </div>
            <div style={styles.switchContainer}>
              <span style={styles.switchLabel}>
                <TruckIcon style={styles.switchIcon} />
                Driver
              </span>
              <input
                type="checkbox"
                checked={roles.isDriver}
                onChange={(e) =>
                  setRoles({ ...roles, isDriver: e.target.checked })
                }
                style={{ cursor: "pointer" }}
              />
            </div>
            <div style={styles.switchContainer}>
              <span style={styles.switchLabel}>
                <WrenchIcon style={styles.switchIcon} />
                Equipment Man
              </span>
              <input
                type="checkbox"
                checked={roles.isEquipmentMan}
                onChange={(e) =>
                  setRoles({ ...roles, isEquipmentMan: e.target.checked })
                }
                style={{ cursor: "pointer" }}
              />
            </div>
            <CustomButton
              title={
                loading
                  ? "Saving..."
                  : editingAction
                  ? "Update Action"
                  : "Create Action"
              }
              onPress={handleSaveAction}
              style={styles.button}
              disabled={loading}
            />
          </div>
        </CustomModal>
        <CustomModal
          isVisible={isPriceModalVisible}
          onClose={() => setPriceModalVisible(false)}
          title="Edit Price"
          style={{
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.gray}`,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
            borderRadius: 12,
            padding: 16,
            maxWidth: 500,
            width: "90%",
            margin: "0 auto",
          }}
        >
          <div style={styles.modalContent}>
            <div style={styles.selectContainer}>
              <span style={styles.selectLabel}>Unit</span>
              <select
                style={styles.select}
                value={editPriceUnit}
                onChange={(e) => setEditPriceUnit(e.target.value)}
                disabled
              >
                <option value={editPriceUnit}>
                  {editPriceUnit.charAt(0).toUpperCase() +
                    editPriceUnit.slice(1)}
                </option>
              </select>
            </div>
            <CustomInput
              label="Price"
              value={editPriceValue}
              onChangeText={setEditPriceValue}
              icon="attach-money"
              type="number"
              placeholder="Enter price"
              style={{ marginBottom: 12 }}
            />
            <CustomButton
              title={loading ? "Saving..." : "Update Price"}
              onPress={handleSavePrice}
              style={styles.button}
              disabled={loading}
            />
          </div>
        </CustomModal>
      </div>
    </div>
  );
};

export default AdminActionManagementScreen;
